import { Link, useParams } from "react-router-dom";
import { Check, ArrowRight, Phone } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { NotFoundContent } from "@/pages/NotFoundPage";
import { findLocation, LOCATIONS } from "@/content/locations";
import { SITE } from "@/content/site";

const VISIT_INCLUDES = [
  "Professional water testing & chemical balancing",
  "Skimming, brushing & vacuuming as needed",
  "Filter checks and scheduled filter cleaning",
  "Equipment inspection on every visit",
  "Saltwater system care for salt pools",
  "Clear communication after every visit",
];

export default function LocationPage() {
  const { slug } = useParams();
  const loc = findLocation(slug);

  if (!loc) return <NotFoundContent />;

  const nearby = LOCATIONS.filter((l) => l.slug !== slug).slice(0, 6);

  return (
    <>
      <SEO
        title={`Pool Service in ${loc.name} FL | Point of Pines Pools & Service`}
        description={`Reliable weekly pool service in ${loc.name}, FL. Professional water chemistry, pool cleaning, equipment care and repairs from a locally owned, CPO certified Pinellas County pool company. Call (727) 744-5908.`}
        path={`/service-areas/${slug}/`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Residential Pool Service",
          provider: {
            "@type": "HomeAndConstructionBusiness",
            name: SITE.name,
            telephone: "+1-727-744-5908",
            url: SITE.domain,
          },
          areaServed: { "@type": "City", name: `${loc.name}, FL` },
        }}
      />
      <PageHero
        overline={`Pool Service — ${loc.name}, FL`}
        title={`Pool Service in`}
        accent={`${loc.name}, Florida`}
        description={loc.short}
      />

      {loc.note && (
        <div data-testid="location-mailer-note" className="bg-mist border-b border-slate-200/70">
          <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-4 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-lagoon shrink-0" />
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-navy">{loc.note.split(".")[0]}.</span>
              {loc.note.split(".").slice(1).join(".")}
            </p>
          </div>
        </div>
      )}

      <section data-testid="location-content-section" className="py-16 sm:py-24 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-[1.15] text-navy">
                Weekly pool cleaning &amp; care for {loc.name} homeowners
              </h2>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">{loc.intro}</p>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">{loc.local}</p>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                Point of Pines Pools &amp; Service is locally owned, CPO certified, and focused
                entirely on residential pools in Pinellas County. When you call, you reach a local
                pro — not a call center.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
                <Link
                  to="/contact/"
                  data-testid="location-quote-button"
                  className="group inline-flex items-center justify-center gap-2.5 bg-aqua text-navy font-bold px-7 py-3.5 rounded-full transition-all duration-300 hover:bg-sky2 hover:text-navy hover:shadow-lg hover:shadow-cyan-500/25"
                >
                  Get a Quote in {loc.name}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <a
                  href={SITE.phoneHref}
                  data-testid="location-call-button"
                  className="inline-flex items-center justify-center gap-2.5 border-2 border-navy text-navy font-bold px-7 py-3.5 rounded-full transition-all duration-300 hover:bg-navy hover:text-white"
                >
                  <Phone className="w-4 h-4" /> {SITE.phone}
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="bg-white rounded-2xl border border-slate-200/80 p-7 sm:p-8">
                <h3 className="font-display text-lg font-semibold text-navy tracking-tight">
                  What every {loc.name} visit includes
                </h3>
                <ul className="mt-5 space-y-3.5">
                  {VISIT_INCLUDES.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-mist flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-pool" />
                      </span>
                      <span className="text-sm text-slate-600 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.18} className="mt-6">
              <div className="bg-mist rounded-2xl p-6">
                <h3 className="text-xs font-mono uppercase tracking-[0.22em] text-pool font-semibold">
                  Also serving nearby
                </h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {nearby.map((n) => (
                    <Link
                      key={n.slug}
                      to={`/service-areas/${n.slug}/`}
                      data-testid={`location-nearby-${n.slug}-link`}
                      className="text-xs font-semibold text-navy bg-white border border-slate-200 rounded-full px-3.5 py-2 transition-colors duration-200 hover:border-aqua hover:text-pool"
                    >
                      {n.name}
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand
        title={`Ready for pool service in ${loc.name}?`}
        text="Request a quote and we'll confirm your service day and walk you through exactly what's included."
      />
    </>
  );
}
